import { useState, useRef, useEffect } from 'react'
import { Dialog, DialogContent } from '@/components/ui/dialog'

interface OtpOverlayProps {
  email: string
  otp: {
    otpStatus: string
    otpError: string | null
    existingApp: { status: string; redirect_to: string } | null
    sendOtp: (email: string) => void
    verifyOtp: (email: string, otp: string) => void
  }
  onClose: () => void
  onVerified: () => void
}

const OTP_LENGTH = 6

// Mirrors .otp-input from app/assets/stylesheets/portal/layout.css —
// the marketing layout doesn't load portal CSS, so the styles live here
// inline so the apply-flow OTP modal matches the portal login pixel-for-pixel.
const otpBoxStyle: React.CSSProperties = {
  width: '3rem',
  height: '3.5rem',
  textAlign: 'center',
  fontSize: '1.5rem',
  fontWeight: 600,
  borderRadius: '0.5rem',
  background: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(255,255,255,0.15)',
  color: '#ffffff',
  transition: 'border-color 0.2s ease, background-color 0.2s ease',
  caretColor: '#ff6a1a',
}

const otpBoxFocusStyle: React.CSSProperties = {
  borderColor: '#ff6a1a',
  background: 'rgba(255,255,255,0.08)',
  outline: 'none',
}

export default function OtpOverlay({ email, otp, onClose, onVerified }: OtpOverlayProps) {
  const [digits, setDigits] = useState<string[]>(() => Array(OTP_LENGTH).fill(''))
  const [focusedIndex, setFocusedIndex] = useState<number | null>(0)
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])
  const code = digits.join('')

  useEffect(() => {
    inputRefs.current[0]?.focus()
  }, [])

  useEffect(() => {
    if (otp.otpStatus === 'verified' && !otp.existingApp) {
      onVerified()
    }
  }, [otp.otpStatus, otp.existingApp])

  function fireVerify(joined: string) {
    if (joined.length === OTP_LENGTH) {
      otp.verifyOtp(email.trim().toLowerCase(), joined)
    }
  }

  function handleChange(index: number, value: string) {
    const digit = value.replace(/\D/g, '').slice(-1)
    setDigits((prev) => {
      const next = [...prev]
      next[index] = digit
      // Auto-advance
      if (digit && index < OTP_LENGTH - 1) {
        inputRefs.current[index + 1]?.focus()
      }
      // Auto-submit on the last digit
      if (digit && index === OTP_LENGTH - 1) {
        const joined = next.join('')
        if (joined.length === OTP_LENGTH) fireVerify(joined)
      }
      return next
    })
  }

  function handleKeyDown(index: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace') {
      if (!digits[index] && index > 0) {
        e.preventDefault()
        inputRefs.current[index - 1]?.focus()
        setDigits((prev) => {
          const next = [...prev]
          next[index - 1] = ''
          return next
        })
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault()
      inputRefs.current[index - 1]?.focus()
    } else if (e.key === 'ArrowRight' && index < OTP_LENGTH - 1) {
      e.preventDefault()
      inputRefs.current[index + 1]?.focus()
    } else if (e.key === 'Enter') {
      e.preventDefault()
      fireVerify(digits.join(''))
    }
  }

  function handlePaste(e: React.ClipboardEvent<HTMLInputElement>) {
    const pasted = (e.clipboardData?.getData('text') || '').replace(/\D/g, '').slice(0, OTP_LENGTH)
    if (!pasted) return
    e.preventDefault()

    const next = Array(OTP_LENGTH).fill('')
    for (let i = 0; i < pasted.length; i++) next[i] = pasted[i]
    setDigits(next)

    const focusTarget = Math.min(pasted.length, OTP_LENGTH - 1)
    inputRefs.current[focusTarget]?.focus()

    if (pasted.length === OTP_LENGTH) fireVerify(pasted)
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    fireVerify(code)
  }

  function handleResend() {
    setDigits(Array(OTP_LENGTH).fill(''))
    inputRefs.current[0]?.focus()
    otp.sendOtp(email.trim().toLowerCase())
  }

  // Existing application found — non-dismissible. Auto-redirects so the
  // applicant can't ignore it and keep filling out a second application.
  // Appending ?from=apply tells the dashboard to surface the "You already
  // have an application" banner.
  if (otp.existingApp) {
    const target = `${otp.existingApp.redirect_to}${otp.existingApp.redirect_to.includes('?') ? '&' : '?'}from=apply`

    return (
      <Dialog open>
        <DialogContent
          title="You already have an application"
          hideTitle
          showClose={false}
          size="sm"
          onEscapeKeyDown={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
        >
          <div className="text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[#ff6a1a]/10">
              <svg className="h-7 w-7 text-[#ff6a1a]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-white">You already have an application</h3>
            <p className="mt-2 text-sm text-white/60">
              Status: <strong className="text-white">{otp.existingApp.status.replace(/_/g, ' ')}</strong>
            </p>
            <p className="mt-3 text-xs text-white/45">
              You can't submit another while this one is open. Take a look at where it stands and edit your details from the status page.
            </p>
            <div className="mt-6">
              <a href={target} className="marketing-button-primary w-full text-center">
                View application status
              </a>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog open onOpenChange={(next) => { if (!next) onClose() }}>
      <DialogContent
        title="Verify your email"
        description={<>We've sent a verification code to <strong className="text-white">{email}</strong></>}
        size="sm"
      >
        <form onSubmit={handleSubmit} className="mt-4 space-y-6">
          <fieldset className="border-0 p-0 m-0">
            <legend className="sr-only">6-digit verification code sent to {email}</legend>
            <div className="flex justify-center gap-2 sm:gap-3">
              {digits.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => { inputRefs.current[index] = el }}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]"
                  maxLength={1}
                  autoComplete={index === 0 ? 'one-time-code' : 'off'}
                  aria-label={`Digit ${index + 1} of ${OTP_LENGTH}`}
                  value={digit}
                  onChange={(e) => handleChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={handlePaste}
                  onFocus={(e) => { e.target.select(); setFocusedIndex(index) }}
                  onBlur={() => setFocusedIndex(null)}
                  style={focusedIndex === index ? { ...otpBoxStyle, ...otpBoxFocusStyle } : otpBoxStyle}
                />
              ))}
            </div>
          </fieldset>

          {otp.otpError && (
            <p className="apply-error-text text-center">{otp.otpError}</p>
          )}

          <div className="pt-4">
            <button
              type="submit"
              disabled={code.length < OTP_LENGTH || otp.otpStatus === 'verifying'}
              className="w-full cta-animated py-3 px-6 rounded-xl font-semibold text-white flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-wait"
            >
              {otp.otpStatus === 'verifying' && (
                <svg className="animate-spin" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                  <circle cx="12" cy="12" r="9" opacity="0.25"/>
                  <path d="M21 12a9 9 0 00-9-9" strokeLinecap="round"/>
                </svg>
              )}
              {otp.otpStatus === 'verifying' ? 'Verifying…' : 'Verify Code'}
            </button>
          </div>
        </form>

        <div className="mt-4 text-center">
          <button
            type="button"
            onClick={handleResend}
            disabled={otp.otpStatus === 'sending'}
            className="text-sm text-[#ff6a1a] hover:text-[#ff8a4a] disabled:opacity-50 transition-colors"
          >
            {otp.otpStatus === 'sending' ? 'Sending...' : 'Resend code'}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
